import classNames from 'classnames'
import { logUtil } from 'packages/src'
import React from 'react'
import { usePreviousHookUtil } from 'modules/component/utils/hooks/usePrevious.js'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import './index.css'
import { isMobileScreenViewportSelect } from 'modules/viewport/selectors/index.js'

let videojs

/**
 * @props type
 * @param props.type
 * @param props.onClick
 * @param props.source
 * @param props.poster
 * @param props.isBig
 * @param props.shouldAutoPlay
 * @param props.hasControls
 * @param props.isMuted
 * @param props.isLooping
 * @returns {Element}
 * @constructor
 */
export default function Video (props) {
  const {
    type,
    onClick,
    source,
    poster,
    isBig,
    shouldAutoPlay = false,
    hasControls = true,
    isMuted = true,
    isLooping = true
  } = props

  const muted = !!(isMuted || shouldAutoPlay)
  const options = {}
  if (isMuted) {
    options.controlBar = {
      volumePanel: false
    }
  }

  const isMobileScreen = useSelector(isMobileScreenViewportSelect)
  const videoRef = React.useRef()
  const playerRef = React.useRef()
  const isMountedRef = React.useRef()
  const prevSource = usePreviousHookUtil(source)
  const hasUpdatedSource = prevSource && source !== prevSource

  const handleUnmount = () => {
    logUtil('Video#handleUnmount').debug('[start]')
    isMountedRef.current = false
    disposePlayer()
  }

  React.useEffect(() => {
    isMountedRef.current = true
    initVideoJs()
    return handleUnmount
  }, [])

  // const play = () => {
  //   playerRef.current.play()
  // }

  const handleUpdateSource = () => {
    if (playerRef.current && hasUpdatedSource) {
      const newSource = getSources()[0]
      logUtil('Video#handleUpdateSource').debug('newSource=', newSource)
      // disposePlayer()
      // initVideo()
      playerRef.current.src(source)
      playerRef.current.poster(poster)
      // shouldAutoPlay && play()
    }
  }

  React.useEffect(
    handleUpdateSource,
    [hasUpdatedSource]
  )

  const disposePlayer = () => {
    logUtil('Video#disposePlayer').debug('[start]')
    if (!playerRef.current || playerRef.current?.isDisposed?.()) {
      return
    }
    playerRef.current.dispose()
    playerRef.current = null
  }

  const getSources = () => {
    return [
      {
        type: 'video/mp4',
        src: source
      }
    ]
  }

  const getOption = () => {
    return {
      techOrder: ['html5'],
      controls: hasControls,
      autoplay: shouldAutoPlay,
      loop: isLooping,
      muted,
      preload: 'auto',
      poster,
      sources: getSources(),
      ...options
    }
  }

  const initVideo = () => {
    if (!videoRef.current || !isMountedRef.current) {
      return
    }

    playerRef.current = videojs(videoRef.current, getOption())
    // if (shouldAutoPlay) {
    //   play()
    // }
  }

  const handleClick = () => {
    if (onClick) {
      onClick()
    }
  }

  const initVideoJs = async () => {
    if (!videojs) {
      const [videojsModule] = await Promise.all([
        import(/* webpackChunkName: "video-js" */ 'video.js'),
        import(/* webpackChunkName: "video-js" */ 'video.js/dist/video-js.css')
      ])
      videojs = videojsModule.default
    }
    initVideo()
  }

  const debugProps = {
    type,
    source,
    poster,
    isMountedRef,
    hasUpdatedSource,
    options,
    muted,
    props
  }
  logUtil('Video').debug('debugProps=', debugProps)

  let width
  let height
  if (props.width) {
    width = props.width
    height = isBig ? '26.66em' : '20em'
  } else if (isMobileScreen) {
    width = '100%'
    height = 'calc(100vw / 1.777777778)'
  } else {
    width = isBig ? '47.44em' : '35.55em'
    height = isBig ? '26.66em' : '20em'
  }

  const className = classNames({
    'video-js': true,
    'vjs-fill': true,
    'vjs-default-skin': true,
    'vjs-big-play-centered': true
  })

  return (
    <div
      style={{
        maxWidth: '100%',
        position: 'relative',
        width,
        height
      }}
    >
      <video
        className={className}
        ref={videoRef}
        onClick={handleClick}
      />
    </div>
  )
}
